import type { CpmPlatformRateEntry } from './cpmOverrideOptions';
import { considerCpmPlatforms, type CpmRatePlatform } from './describeCampaignCpmRates';

/** The admin form's "CPM diferente por rede social" section. Blank = base rate. */
export type CpmPlatformRatesFormState = {
  enabled: boolean;
  rates: Partial<Record<CpmRatePlatform, number | null>>;
};

const CPM_PLATFORMS: readonly CpmRatePlatform[] = ['youtube', 'instagram', 'tiktok'];

const isRate = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) > 0;

export function cpmPlatformRatesFormFromCampaign(
  rates: readonly CpmPlatformRateEntry[] | null | undefined,
): CpmPlatformRatesFormState {
  const stored: CpmPlatformRatesFormState['rates'] = {};
  (rates ?? []).forEach((rate) => {
    if (CPM_PLATFORMS.includes(rate.platform as CpmRatePlatform) && isRate(rate.cpm_rate_cents)) {
      stored[rate.platform as CpmRatePlatform] = rate.cpm_rate_cents;
    }
  });
  return { enabled: Object.keys(stored).length > 0, rates: stored };
}

/**
 * What the save sends as `cpm_platform_rates`: `[]` clears the rates. Rates for
 * networks the campaign does not accept are left out, so unticking a network
 * never trips the backend's `cpm_platform_rate_platform_not_allowed`.
 */
export function cpmPlatformRatesInput(
  state: CpmPlatformRatesFormState,
  allowedPlatforms: readonly string[] | null | undefined,
): CpmPlatformRateEntry[] {
  if (!state.enabled) return [];
  return considerCpmPlatforms(allowedPlatforms)
    .filter((platform) => isRate(state.rates[platform]))
    .map((platform) => ({ platform, cpm_rate_cents: state.rates[platform] as number }));
}

/**
 * Accepted networks whose rate was typed as R$ 0,00. A network that should not
 * pay is unticked from "Plataformas permitidas"; a zero would otherwise be
 * dropped and silently pay the base rate, so the form blocks the save.
 */
export function zeroCpmPlatformRates(
  state: CpmPlatformRatesFormState,
  allowedPlatforms: readonly string[] | null | undefined,
): CpmRatePlatform[] {
  if (!state.enabled) return [];
  return considerCpmPlatforms(allowedPlatforms).filter((platform) => state.rates[platform] === 0);
}
